import type { ReactNode } from 'react';

/**
 * The full icon registry backing `<Icon>` (DS-07). Each entry renders the
 * inner SVG markup only — `<Icon>` owns the `<svg>` wrapper, viewBox,
 * stroke/fill defaults and accessibility attributes.
 *
 * Path data for `planet`, `fleets`, `galaxy`, `personal`, `alliance`,
 * `ranking`, `metal`, `mineral`, `gas`, `energy` and `population` is copied
 * verbatim from the design-system mockups (`Planet Surface Flat.dc.html`)
 * per issue #177 — do not redraw these.
 */
export const ICON_PATHS = {
    // Navigation (6)
    planet: (
        <>
            <circle cx="12" cy="12" r="6" />
            <ellipse cx="12" cy="12" rx="10" ry="3.2" />
        </>
    ),
    fleets: (
        <>
            <path d="M12 2L18 14H6L12 2Z" />
            <path d="M6 14L4 20H20L18 14" />
        </>
    ),
    galaxy: <path d="M12 3a9 9 0 100 18 6 6 0 100-12 3 3 0 100 6" />,
    personal: (
        <>
            <circle cx="12" cy="8" r="3.6" />
            <path d="M5 20c0-4 3.2-6.5 7-6.5S19 16 19 20" />
        </>
    ),
    // Two figures, not the shield `zone-military` uses — the two were the
    // same path, so nav and zone strip drew the same glyph.
    alliance: (
        <>
            <circle cx="9" cy="8" r="3.5" />
            <circle cx="17" cy="9.5" r="2.5" />
            <path d="M2.5 20C2.5 16 5.4 13.5 9 13.5C12.6 13.5 15.5 16 15.5 20" />
            <path d="M16.5 14C19.3 14 21.5 16.3 21.5 19.5" strokeLinecap="round" />
        </>
    ),
    ranking: <path d="M8 21V13H4V21H8ZM14 21V9H10V21H14ZM20 21V15H16V21H20Z" />,

    // Resources (5)
    metal: <path d="M12 2 L21 7 L21 17 L12 22 L3 17 L3 7 Z" />,
    mineral: <path d="M6 3H18L22 9L12 21L2 9L6 3Z" />,
    gas: <path d="M12 2C12 2 5 11 5 15.5C5 19.6 8.1 22 12 22C15.9 22 19 19.6 19 15.5C19 11 12 2 12 2Z" />,
    energy: <path d="M13 2L4 14H11L10 22L20 9H13L13 2Z" fill="currentColor" stroke="none" />,
    population: (
        <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21C4 16.6 7.6 14 12 14C16.4 14 20 16.6 20 21" />
        </>
    ),
    // Recycling triangle, drawn in the same stroke language as the five
    // resource glyphs above. Not in the mockup's set — the Scrap tile has
    // no mockup counterpart — so this one is ours.
    scrap: (
        <>
            <path d="M7 7L4.5 11.5L7.5 13" strokeLinecap="round" />
            <path d="M12 3.5L14.5 8L11.5 9.5" strokeLinecap="round" />
            <path d="M19 13L20 18L16.5 18" strokeLinecap="round" />
            <path d="M4.5 11.5L6.5 19H12" strokeLinecap="round" />
            <path d="M14.5 8L19 13" strokeLinecap="round" />
            <path d="M12 19L16.5 18" strokeLinecap="round" />
        </>
    ),

    // Zones (3)
    // An axe, not the droplet `gas` uses — the two were the same path.
    'zone-resource': (
        <>
            <path d="M6.5 19.5L15 8" strokeLinecap="round" />
            <path d="M12.5 5.5L16 2.5C19.5 5 20.5 9 19.5 12.5L14.5 10.5Z" strokeLinejoin="round" />
        </>
    ),
    'zone-industry': <path d="M4 21V10L12 4L20 10V21H14V15H10V21H4Z" />,
    'zone-military': <path d="M12 2L20 5V11C20 16 16.5 20 12 22C7.5 20 4 16 4 11V5L12 2Z" />,

    spy: (
        <>
            <circle cx="8" cy="12" r="5" />
            <circle cx="16" cy="12" r="5" />
            <path d="M11 9H13" />
        </>
    ),

    // Currency (2)
    'dark-matter': <path d="M12 2L20 12L12 22L4 12Z" />,
    'trade-token': (
        <>
            <path d="M4 8H17L14 5" />
            <path d="M20 16H7L10 19" />
        </>
    ),

    // Ship stats (4). Hangar cost reuses `population`.
    attack: (
        <>
            <path d="M21 3L11 13" />
            <path d="M7.5 9.5L14.5 16.5" />
            <path d="M11 13L5.5 18.5" />
        </>
    ),
    life: <path d="M12 20.5C12 20.5 4 15.5 4 9.6A4.4 4.4 0 0 1 12 7.2A4.4 4.4 0 0 1 20 9.6C20 15.5 12 20.5 12 20.5Z" />,
    cargo: (
        <>
            <path d="M3 4H21V9H3Z" />
            <path d="M5 9V20H19V9" />
            <path d="M10 13H14" />
        </>
    ),
    speed: (
        <>
            <path d="M5 6L11 12L5 18" />
            <path d="M13 6L19 12L13 18" />
        </>
    ),

    // Overlay & nav (8)
    science: (
        <>
            <path d="M9 3H15" />
            <path d="M10 3V9L4.5 19A2 2 0 0 0 6.5 21H17.5A2 2 0 0 0 19.5 19L14 9V3" />
            <path d="M7.5 15H16.5" />
        </>
    ),
    // An anchor, for the Factory nav link — distinct from `fleets`, which is
    // a rocket silhouette used for the ships-in-transit destination.
    shipyard: (
        <>
            <circle cx="12" cy="5" r="2" />
            <path d="M12 7V17" />
            <path d="M8 12H16" />
            <path d="M6 15C6 18 9 20 12 20C15 20 18 18 18 15" strokeLinecap="round" />
        </>
    ),
    starport: (
        <>
            <path d="M6 14L12 8L18 14" />
            <path d="M6 19L12 13L18 19" />
        </>
    ),
    'alliance-leader': (
        <>
            <path d="M4 18H20L21 9L16 13L12 6L8 13L3 9Z" />
            <path d="M4 18V20H20V18" />
        </>
    ),
    // A targeting reticle, not a literal bomb — a circle-plus-diagonal bomb
    // silhouette rendered indistinguishable from the ♂ glyph at this stroke
    // weight.
    destroy: (
        <>
            <circle cx="12" cy="12" r="7" />
            <path d="M12 1.5V5.5" />
            <path d="M12 18.5V22.5" />
            <path d="M1.5 12H5.5" />
            <path d="M18.5 12H22.5" />
        </>
    ),
    gift: (
        <>
            <path d="M4 9H20V20H4Z" />
            <path d="M4 9V6H20V9" />
            <path d="M12 6V20" />
            <path d="M9 6C9 4 10 3 12 4C14 3 15 4 15 6" />
        </>
    ),
    // Fill is left to the caller (`fill="currentColor"` for a saved/filled
    // state) rather than shipping a second `star-filled` entry.
    star: <path d="M12 3L14.5 9.5L21 10L16 14.5L17.5 21L12 17.3L6.5 21L8 14.5L3 10L9.5 9.5Z" />,
    asteroid: (
        <>
            <path d="M14.5 5C18 5.3 20 8 19.3 11.3C18.6 14.3 15.4 15.4 12.8 14.2C10.4 13.1 9.8 9.6 11.8 7.1C12.6 6.1 13.4 5.4 14.5 5Z" />
            <path d="M13.5 8L13.51 8" strokeLinecap="round" strokeWidth="2.2" />
            <path d="M16 11L16.01 11" strokeLinecap="round" strokeWidth="2.2" />
            <path d="M2.5 21L6 18.5" strokeLinecap="round" />
            <path d="M5 22L7.8 20" strokeLinecap="round" />
            <path d="M7.3 22.5L9.5 21" strokeLinecap="round" />
        </>
    ),

    // Utility (9)
    close: (
        <>
            <path d="M5 5L19 19" />
            <path d="M19 5L5 19" />
        </>
    ),
    'chevron-left': <path d="M15 4L7 12L15 20" />,
    'chevron-right': <path d="M9 4L17 12L9 20" />,
    plus: (
        <>
            <path d="M12 5V19" />
            <path d="M5 12H19" />
        </>
    ),
    minus: <path d="M5 12H19" />,
    check: <path d="M4 12L10 18L20 6" />,
    lock: (
        <>
            <path d="M5 11H19V21H5Z" />
            <path d="M8 11V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V11" />
            <path d="M12 15V17" />
        </>
    ),
    alert: (
        <>
            <path d="M12 2L22 20H2L12 2Z" />
            <path d="M12 9V14" />
            <path d="M12 17.5V17.6" />
        </>
    ),
    reorder: (
        <>
            <path d="M4 8H20" />
            <path d="M4 12H20" />
            <path d="M4 16H20" />
            <circle cx="9" cy="8" r="1" fill="currentColor" stroke="none" />
            <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="9" cy="16" r="1" fill="currentColor" stroke="none" />
        </>
    ),
    // Diagonal medicine capsule: rounded body on a 45 degree axis, seam across the middle.
    capsule: (
        <>
            <path d="M3.51 14.12L14.12 3.51A4.5 4.5 0 0 1 20.49 9.88L9.88 20.49A4.5 4.5 0 0 1 3.51 14.12Z" />
            <path d="M8.82 8.82L15.18 15.18" />
        </>
    ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof ICON_PATHS;

export const ICON_NAMES = Object.keys(ICON_PATHS) as IconName[];
